import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, INR, INR2, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Coins, TrendingUp, TrendingDown, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const UNIT_CHIPS = [1, 5, 10, 25, 50];

export default function BuyEtfModal({ open, onOpenChange, etf, onDone }) {
  const { user } = useAuth();
  const [units, setUnits] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setUnits(1); }, [open]);

  if (!etf) return null;
  const amount = Number(units || 0) * Number(etf.ltp || 0);
  const up = (etf.chg_pct || 0) >= 0;

  const finish = async (order_id, payment_id, signature) => {
    try {
      const { data } = await api.post("/etfs/buy/verify", {
        razorpay_order_id: order_id,
        razorpay_payment_id: payment_id,
        razorpay_signature: signature,
      });
      toast.success(`Bought ${data.units_added} units of ${etf.symbol}`);
      onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data: order } = await api.post("/etfs/buy/order", {
        etf_id: etf.id,
        units: Number(units),
      });
      if (order.mock) {
        await finish(order.order_id, "pay_mock_123", "mock_signature");
        return;
      }
      if (typeof window.Razorpay === "undefined") {
        toast.error("Payment SDK didn't load. Refresh and retry.");
        return;
      }
      const rzp = new window.Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount_paise,
        currency: "INR",
        name: "ShreeDhan",
        description: `Buy ${etf.symbol} ${units} units`,
        theme: { color: "#C9922A" },
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone ? (user.phone.startsWith("+") ? user.phone : `+91${user.phone}`) : "",
        },
        handler: (r) => finish(r.razorpay_order_id, r.razorpay_payment_id, r.razorpay_signature),
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="buy-etf-modal" className="max-w-md rounded-3xl bg-brand-sand border-brand-line">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-brand-cream flex items-center gap-2">
            <Coins className="text-brand-primary" size={20} /> Buy {etf.symbol}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-2xl bg-brand-sand2 border border-brand-line-faint p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-brand-cream truncate">{etf.name}</div>
              <div className="text-[11px] text-brand-slate">{etf.amc} · {etf.symbol}</div>
              <div className="mt-2 flex items-center gap-2 text-[11px]">
                <span className="rounded-full bg-brand-secondary/20 text-brand-secondary px-2 py-0.5 font-semibold">
                  Govt-backed
                </span>
                <span className="text-brand-slate">Exp. {etf.exp_ratio}%</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-display text-2xl text-brand-cream tabular-nums">{INR2(etf.ltp)}</div>
              <div className={`text-[11px] font-semibold flex items-center gap-1 justify-end ${up ? "text-brand-secondary" : "text-brand-danger"}`}>
                {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {etf.chg_pct}%
              </div>
            </div>
          </div>
        </div>

        <div>
          <Label className="text-brand-cream text-sm font-semibold">Units</Label>
          <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
            {UNIT_CHIPS.map((c) => (
              <button
                key={c}
                data-testid={`etf-units-${c}`}
                onClick={() => setUnits(c)}
                className={`shrink-0 h-11 px-4 rounded-full text-sm font-semibold border transition-transform active:scale-95 ${
                  Number(units) === c
                    ? "bg-brand-primary text-brand-page border-brand-primary"
                    : "bg-brand-sand2 text-brand-cream border-brand-line-faint"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <Input
            data-testid="etf-units-input"
            type="number"
            min="1"
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            className="mt-3 h-12 rounded-xl bg-brand-sand2 border-brand-line-faint tabular-nums text-brand-cream"
          />
        </div>

        <div className="rounded-2xl bg-brand-sand2 border border-brand-line text-brand-cream p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-brand-slate">Order value</span>
            <span className="font-semibold tabular-nums text-brand-accent">{INR(amount)}</span>
          </div>
          <div className="mt-1 flex items-center justify-between text-sm">
            <span className="text-brand-slate">You get</span>
            <span className="font-semibold tabular-nums text-brand-cream">{units} × {INR2(etf.ltp)}</span>
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-brand-slate2">
            <ShieldCheck size={12} /> SEBI-regulated · settles T+1 · demat units
          </div>
        </div>

        <Button
          data-testid="etf-buy-submit"
          onClick={submit}
          disabled={loading || !units || units <= 0}
          className="w-full h-14 rounded-full bg-gold-gradient text-brand-page font-semibold text-base shadow-gold"
        >
          {loading ? "Processing…" : `Pay ${INR(amount)}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
